import { HStack, Button, Text, Select, createListCollection } from '@chakra-ui/react';

interface PaginationProps {
  currentPage: number;
  totalPages?: number; // Total pages from API (preferred)
  totalItems?: number; // Total items for display and fallback calculation
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  isLoading?: boolean;
  hasMore?: boolean; // Whether there are more items to load
}

const Pagination = ({
  currentPage,
  totalPages: totalPagesFromAPI,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  isLoading = false,
}: PaginationProps) => {
  // Prefer totalPages from API, fallback to calculation if provided totalItems
  const totalPages =
    totalPagesFromAPI ?? (totalItems ? Math.ceil(totalItems / itemsPerPage) : undefined);

  const goToPage = (page: number) => {
    if (page >= 1 && (!totalPages || page <= totalPages)) {
      onPageChange(page);
    }
  };

  const renderPageNumbers = () => {
    if (!totalPages) {
      // When we don't know total pages, only show current page
      return [
        <Button
          key={currentPage}
          size="sm"
          variant="solid"
          onClick={() => goToPage(currentPage)}
          disabled={isLoading}
        >
          {currentPage}
        </Button>,
      ];
    }

    const pages = [];

    // Always show page 1
    if (currentPage > 3) {
      pages.push(
        <Button
          key={1}
          size="sm"
          variant="outline"
          onClick={() => goToPage(1)}
          disabled={isLoading}
        >
          1
        </Button>
      );

      // Show ellipsis if there's a gap
      if (currentPage > 4) {
        pages.push(
          <Text key="ellipsis1" fontSize="sm" px={2}>
            ...
          </Text>
        );
      }
    }

    // Show pages around current page
    const startPage = Math.max(1, currentPage - 1);
    const endPage = Math.min(totalPages, currentPage + 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          size="sm"
          variant={i === currentPage ? 'solid' : 'outline'}
          onClick={() => goToPage(i)}
          disabled={isLoading}
        >
          {i}
        </Button>
      );
    }

    // Show ellipsis and last page if needed
    if (currentPage < totalPages - 2) {
      if (currentPage < totalPages - 3) {
        pages.push(
          <Text key="ellipsis2" fontSize="sm" px={2}>
            ...
          </Text>
        );
      }

      pages.push(
        <Button
          key={totalPages}
          size="sm"
          variant="outline"
          onClick={() => goToPage(totalPages)}
          disabled={isLoading}
        >
          {totalPages}
        </Button>
      );
    }

    return pages;
  };

  const itemsPerPageOptions = createListCollection({
    items: [
      { label: '5', value: '5' },
      { label: '10', value: '10' },
      { label: '25', value: '25' },
      { label: '50', value: '50' },
    ],
  });

  return (
    <HStack justify="space-between" align="center" w="full">
      <HStack>
        <Text fontSize="sm">Visa</Text>
        <Select.Root
          collection={itemsPerPageOptions}
          size="sm"
          value={[itemsPerPage.toString()]}
          onValueChange={details => onItemsPerPageChange(parseInt(details.value[0]))}
          width="80px"
          positioning={{ strategy: 'fixed' }}
        >
          <Select.Trigger>
            <Select.ValueText />
          </Select.Trigger>
          <Select.Positioner>
            <Select.Content minW="80px">
              {itemsPerPageOptions.items.map(item => (
                <Select.Item key={item.value} item={item}>
                  {item.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
        <Text fontSize="sm">rader</Text>
      </HStack>

      <HStack justify="center" position="absolute" left="50%" transform="translateX(-50%)">
        {renderPageNumbers()}
      </HStack>
    </HStack>
  );
};

export default Pagination;
